import { inject, Service } from '@angular/core';
import type { OnyxOption, OnyxOptional } from '@onyx/angular';
import { chain } from 'lodash';
import type { Observable } from 'rxjs';
import { forkJoin, map, of, shareReplay } from 'rxjs';
import { AuthStore } from '../../auth/common/stores/auth.store';
import type { FleetCategory } from '../../dashboard/fleet/common/enums/fleet-category';
import { VehiclePlanningMode } from '../../dashboard/fleet/common/enums/vehicle-planning-mode';
import { PaymentTermType } from '../../dashboard/management-panel/contractors/common/enums/payment-term-type';
import { EmployeeRole } from '../../dashboard/management-panel/employees/common/enums/employee-role';
import { RoadPricingType } from '../../dashboard/management-panel/route-costs-and-preferences/common/enums/road-pricing-type';
import { OrderPointCategory } from '../../dashboard/orders/common/enums/order-point-category';
import { OrderStatus } from '../../dashboard/orders/common/enums/order-status';
import { OrderHelper } from '../../dashboard/orders/common/helpers/order.helper';
import { WEEKDAYS } from '../constants/common/weekdays';
import type {
  DictionaryResponseByCode,
  DictionaryType,
} from '../enums/dictionary-code';
import { DictionaryCode } from '../enums/dictionary-code';
import type { FuelType } from '../enums/fuel-type';
import { InterfaceLanguage } from '../enums/interface-language';
import { DictionariesStorageKey } from '../enums/storage/dictionaries-storage-key';
import { CommonHelper } from '../helpers/common.helper';
import type { Weekdays } from '../interfaces/common/weekdays';
import { ApiService } from './api.service';
import { CacheService } from './cache.service';
import { LanguageService } from './language.service';
import { PreferencesService } from './preferences.service';

@Service()
export class DictionariesService extends ApiService {
  private readonly authStore = inject(AuthStore);
  private readonly languageService = inject(LanguageService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly cacheService = inject(CacheService);
  private readonly orderHelper = inject(OrderHelper);

  public getDictionary<T extends DictionaryCode>(
    code: T,
    { icons = true }: { icons?: boolean } = {},
  ): Observable<DictionaryType<T>> {
    const language = this.languageService.getLanguage();
    const cacheKey = `${DictionariesStorageKey.DICTIONARY}-${code}-${language}`;

    // TEMP: Wait for BE implementation
    if (code === DictionaryCode.ROAD_PRICING_TYPE) {
      return of([
        {
          name: 'Rabat',
          value: RoadPricingType.DISCOUNT,
        },
        {
          name: 'Prowizja',
          value: RoadPricingType.COMMISSION,
        },
      ] satisfies DictionaryType<DictionaryCode.ROAD_PRICING_TYPE> as DictionaryType<T>);
    }
    // END TEMP

    return this.cacheService
      .cacheRequest(
        cacheKey,
        this.get<DictionaryResponseByCode[T][]>(
          `/dictionaries/${code}/${language}`,
        ).pipe(
          map((dictionary) => {
            switch (code) {
              // TEMP: Restore when translations ready
              case DictionaryCode.INTERFACE_LANGUAGE:
                return (
                  dictionary as DictionaryResponseByCode[DictionaryCode.INTERFACE_LANGUAGE][]
                ).filter(({ value }) =>
                  CommonHelper.isEnum(InterfaceLanguage, value),
                );

              // TEMP: Wait for Engine transit points support
              case DictionaryCode.ORDER_POINT_TYPE:
                return (
                  dictionary as DictionaryResponseByCode[DictionaryCode.ORDER_POINT_TYPE][]
                ).filter(
                  ({ category }) => category !== OrderPointCategory.TRANSIT,
                );

              default:
                return dictionary;
            }
          }),
        ),
      )
      .pipe(
        map((dictionary) => {
          if (!icons) return dictionary;

          return dictionary.map((option) => {
            switch (code) {
              case DictionaryCode.ADR_CLASS:
                return {
                  ...option,
                  leftIcon: { name: option.value, size: 20 },
                };

              case DictionaryCode.CONTRACTOR_STATUS:
              case DictionaryCode.EMPLOYEE_STATUS:
              case DictionaryCode.FLEET_STATE: {
                const getDotColor = () => {
                  switch (option.value) {
                    case 'active':
                      return 'green';
                    case 'blocked':
                      return 'red';
                    case 'unavailable':
                      return 'red';
                    case 'invited':
                      return 'yellow';
                    default:
                      return 'gray-alt';
                  }
                };
                return {
                  ...option,
                  leftDotColor: getDotColor(),
                };
              }

              case DictionaryCode.COUNTRY:
                return {
                  ...option,
                  leftFlag: option.value,
                };

              case DictionaryCode.CURRENCY:
                return {
                  ...option,
                  leftFlag: (
                    option as DictionaryResponseByCode[DictionaryCode.CURRENCY]
                  ).flag,
                };

              case DictionaryCode.DRIVER_UNAVAILABILITY_REASON:
              case DictionaryCode.EMPLOYEE_UNAVAILABILITY_REASON:
                return {
                  ...option,
                  leftIcon: {
                    name: `${option.value}-outlined`,
                    size: 20,
                  },
                };

              case DictionaryCode.ORDER_POINT_TYPE:
                return {
                  ...option,
                  leftIcon: {
                    name: (
                      option as DictionaryResponseByCode[DictionaryCode.ORDER_POINT_TYPE]
                    ).icon,
                    size: 20,
                  },
                  leftIconColor: (
                    option as DictionaryResponseByCode[DictionaryCode.ORDER_POINT_TYPE]
                  ).color,
                };

              case DictionaryCode.ORDER_STATUS:
                return {
                  ...option,
                  leftIcon: {
                    name: this.orderHelper.getStatusIcon(
                      (
                        option as DictionaryResponseByCode[DictionaryCode.ORDER_STATUS]
                      ).value,
                      'outlined',
                    ),
                    size: 20,
                  },
                };

              case DictionaryCode.SEMI_TRAILER_SIZE:
                return {
                  ...option,
                  leftIcon: {
                    name: `semi-trailer-${option.value}-outlined`,
                    size: 20,
                  },
                  leftIconColor: 'blue',
                };

              case DictionaryCode.TACHOGRAPH_WORKING_MODE:
                return {
                  ...option,
                  leftIcon: {
                    name: `tacho-${option.value}-outlined`,
                    size: 20,
                  },
                };

              case DictionaryCode.TRAILER_CATEGORY:
              case DictionaryCode.VEHICLE_CATEGORY:
                return {
                  ...option,
                  leftIcon: { name: `${option.value}-filled`, size: 20 },
                };

              case DictionaryCode.VEHICLE_PLANNING_MODE:
                return {
                  ...option,
                  leftIcon: {
                    name:
                      option.value === VehiclePlanningMode.AUTO
                        ? 'signet-light'
                        : 'manual-outlined',
                    size: 20,
                  },
                  leftIconColor:
                    option.value === VehiclePlanningMode.AUTO
                      ? 'black'
                      : 'violet',
                };

              default:
                return option;
            }
          });
        }),
        map((dictionary) => {
          switch (code) {
            case DictionaryCode.CURRENCY:
              return chain(dictionary)
                .groupBy(
                  ({ value }) =>
                    value === this.preferencesService.defaultCurrency(),
                )
                .thru((groups) => [groups['true'], groups['false']])
                .compact()
                .map((options) => ({ options }))
                .value();

            case DictionaryCode.ORDER_STATUS: {
              const GROUPS: OrderStatus[][] = [
                [OrderStatus.DRAFT],
                [OrderStatus.VEHICLE_SEARCH, OrderStatus.TO_ACCEPT],
                [OrderStatus.TO_DO],
                [OrderStatus.IN_PROGRESS],
                [
                  OrderStatus.COMPLETING_DOCUMENTS,
                  OrderStatus.INVOICE_TO_ISSUE,
                  OrderStatus.WAITING_FOR_PAYMENT,
                  OrderStatus.PARTIALLY_PAID,
                  OrderStatus.PAID,
                ],
                [OrderStatus.CANCELED, OrderStatus.ARCHIVED],
              ] as const;

              const options =
                dictionary as DictionaryResponseByCode[DictionaryCode.ORDER_STATUS][];
              return GROUPS.map((group) => ({
                options: options.filter(({ value }) => group.includes(value)),
              }));
            }

            case DictionaryCode.PAYMENT_TERM_TYPE: {
              const GROUPS: PaymentTermType[][] = [
                [
                  PaymentTermType.LAST_UNLOADING,
                  PaymentTermType.INVOICE_DELIVERY,
                ],
                [
                  PaymentTermType.LAST_DAY_OF_MONTH,
                  PaymentTermType.FIRST_DAY_OF_MONTH,
                  PaymentTermType.SELECTED_DAY,
                ],
                Object.values(WEEKDAYS) as PaymentTermType[],
              ] as const;

              const options =
                dictionary as DictionaryResponseByCode[DictionaryCode.PAYMENT_TERM_TYPE][];
              return GROUPS.map((group) => ({
                options: options.filter(({ value }) => group.includes(value)),
              }));
            }

            case DictionaryCode.COLLECTIVE_INVOICE_ISSUE_TERM: {
              const options =
                dictionary as DictionaryResponseByCode[DictionaryCode.COLLECTIVE_INVOICE_ISSUE_TERM][];
              return [false, true].map((includesWeekdays) => ({
                options: options.filter(
                  ({ value }) =>
                    WEEKDAYS.includes(value as keyof Weekdays) ===
                    includesWeekdays,
                ),
              }));
            }

            default:
              return dictionary;
          }
        }),
        map((dictionary) => dictionary as DictionaryType<T>),
        shareReplay(1),
      );
  }

  public getAvailableEmployeeRoles(): Observable<
    DictionaryType<DictionaryCode.EMPLOYEE_ROLE>
  > {
    return this.getDictionary(DictionaryCode.EMPLOYEE_ROLE).pipe(
      map((roles) => {
        const userRoles = this.authStore.user()?.roles;
        const isAdmin = userRoles?.includes(EmployeeRole.BUSINESS_ADMIN);
        const isManager = isAdmin || userRoles?.includes(EmployeeRole.MANAGER);

        return roles.filter((role) => {
          if (role.value === EmployeeRole.BUSINESS_ADMIN) return isAdmin;
          if (role.value === EmployeeRole.MANAGER) return isAdmin;
          return isManager;
        });
      }),
      shareReplay(1),
    );
  }

  public getFleetCategories({
    icons = true,
  }: { icons?: boolean } = {}): Observable<OnyxOption<FleetCategory>[]> {
    return forkJoin([
      this.getDictionary(DictionaryCode.VEHICLE_CATEGORY, { icons }),
      this.getDictionary(DictionaryCode.TRAILER_CATEGORY, { icons }),
    ]).pipe(
      map((categories) => categories.flat()),
      shareReplay(1),
    );
  }

  public getFuelUnit(fuelType: OnyxOptional<FuelType>): Observable<string> {
    if (!fuelType) return of('??');

    return this.getDictionary(DictionaryCode.FUEL_TYPE, { icons: false }).pipe(
      map(
        (fuelTypes) =>
          fuelTypes.find(({ value }) => value === fuelType)?.units[0] ?? '??',
      ),
      shareReplay(1),
    );
  }

  public getVatIdCountries(): Observable<OnyxOption<string>[]> {
    return this.getDictionary(DictionaryCode.COUNTRY).pipe(
      map((countries) =>
        chain(countries)
          .map((country) => ({
            ...country,
            name: country.value.toUpperCase(),
          }))
          .orderBy('name')
          .value(),
      ),
      shareReplay(1),
    );
  }
}
